/**
 * Shell — persistent app chrome for the web PWA.
 *
 * A sticky top nav wraps every route via a layout route in router.tsx. The nav
 * is view-aware (roleView, derived from the real passkey session): a returning
 * client sees a minimal client nav linking to their dossier; a new/anonymous
 * visitor sees brand-only. (The web app is client-only — staff use the admin
 * console.)
 *
 * Auth *state* drives the top-right action independently of the view: a signed-
 * in user (incl. a client who hasn't started a transaction) gets the avatar; an
 * anonymous visitor gets a Sign in CTA into /login.
 *
 * The ChatDock is mounted here so the concierge follows the client across every
 * route — it hides itself on the new-client home (where the hero already shows
 * the chat full-size).
 */

import { Bell, Home as HomeIcon, LogOut } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";

import { APP_NAME } from "../brand";
import { clearSession, loadSession } from "../features/auth/session";
import {
  HostBrokerageContext,
  useHostBrokerage,
} from "../features/home/customDomain";
import { ChatDock } from "./ChatDock";
import { MobileTabBar } from "./MobileTabBar";
import { useRoleView, type RoleView } from "./roleView";

import { DesignToggle } from "./DesignToggle";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

function navFor(view: RoleView, dossierId: string | null): readonly NavItem[] {
  if (view === "client-returning" && dossierId) {
    return [
      { to: "/", label: "Home", end: true },
      { to: `/dossier/${dossierId}`, label: "My documents" },
      { to: "/agreements", label: "My agreements" },
      { to: "/profile", label: "Profile" },
    ];
  }
  return [];
}

function initials(name: string | null | undefined, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2)
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export function Shell() {
  const { view, authenticated, dossierId, loading } = useRoleView();
  const nav = navFor(view, dossierId);
  const { pathname } = useLocation();
  // Only an anonymous visitor's `/` can be a brokerage's page (spec 011).
  // Resolved here, once, so Home and this bar agree on what `/` shows.
  const hostBrokerage = useHostBrokerage(
    !loading && view === "client-new" && pathname === "/",
  );
  // A brokerage's page carries its own, tenant-scoped Sign in; this link
  // carries no tenant, so it is not offered alongside it (FR-011).
  const showSignIn = hostBrokerage.kind !== "brokerage";
  const session = loadSession();
  const navigate = useNavigate();

  const avatarInitials = initials(session?.displayName, session?.email ?? "");
  const avatarTitle = session?.displayName ?? session?.email ?? "";

  const shellClass = `rp-appshell ${view === "client-returning" ? "rp-appshell--has-tabs" : ""}`;

  return (
    <div className={shellClass}>
      <a href="#main" className="rp-skip">
        Skip to main content
      </a>
      <header className="rp-appbar">
        <div className="rp-appbar__inner">
          <NavLink to="/" className="rp-appbar__brand">
            <span className="rp-appbar__mark" aria-hidden="true">
              <HomeIcon size={16} color="white" strokeWidth={2.4} />
            </span>
            {APP_NAME}
          </NavLink>

          <nav className="rp-appbar__nav">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                viewTransition
                className={({ isActive }) => (isActive ? "active" : undefined)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="rp-appbar__spacer" />

          <div className="rp-appbar__actions">
            {authenticated ? (
              <>
                <button
                  type="button"
                  className="rp-appbar__icon"
                  aria-label="Notifications"
                  title="Notifications (Phase 5)"
                >
                  <Bell size={16} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="rp-appbar__avatar"
                  title={`${avatarTitle} — Profile`}
                  onClick={() => navigate("/profile")}
                  style={{ cursor: "pointer" }}
                >
                  {avatarInitials}
                </button>
                <button
                  type="button"
                  className="rp-appbar__icon"
                  aria-label="Sign out"
                  title="Sign out"
                  onClick={() => {
                    clearSession();
                    // Full reload so RoleViewProvider re-reads the (now empty)
                    // session and the shell returns to the signed-out view.
                    window.location.assign("/");
                  }}
                >
                  <LogOut size={16} strokeWidth={2} />
                </button>
              </>
            ) : (
              /*<NavLink to="/login" className="rp-appbar__signin">
                Sign in
              </NavLink>*/
              <div className="rp-appbar__actions">
                <DesignToggle />
                {authenticated ? (
                  <>
                    <button
                      type="button"
                      className="rp-appbar__icon"
                      aria-label="Notifications"
                      title="Notifications (Phase 5)"
                    >
                      <Bell size={16} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      className="rp-appbar__avatar"
                      title={`${avatarTitle} — Profile`}
                      onClick={() => navigate("/profile")}
                      style={{ cursor: "pointer" }}
                    >
                      {avatarInitials}
                    </button>
                    <button
                      type="button"
                      className="rp-appbar__icon"
                      aria-label="Sign out"
                      title="Sign out"
                      onClick={() => {
                        clearSession();
                        window.location.assign("/");
                      }}
                    >
                      <LogOut size={16} strokeWidth={2} />
                    </button>
                  </>
                ) : showSignIn ? (
                  <NavLink to="/login" className="rp-appbar__signin">
                    Sign in
                  </NavLink>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </header>

      <main id="main" className="rp-outlet">
        <HostBrokerageContext.Provider value={hostBrokerage}>
          <Outlet />
        </HostBrokerageContext.Provider>
      </main>

      <ChatDock />
      <MobileTabBar />
    </div>
  );
}
